export const STOCK_REDUCTION_REASON_THRESHOLD = 3;

export const STOCK_REDUCTION_REASON_FIELDS = {
	MISSING: 'missingQuantity',
	SPOILED: 'spoiledQuantity',
	REMOVED: 'removedQuantity',
	NOT_ON_PICKLIST: 'notPickedQuantity',
};

export const STOCK_REDUCTION_REASON_OPTIONS = [
	{
		field: STOCK_REDUCTION_REASON_FIELDS.MISSING,
		label: 'Missing / unknown',
	},
	{
		field: STOCK_REDUCTION_REASON_FIELDS.SPOILED,
		label: 'Spoiled / out of date',
	},
	{
		field: STOCK_REDUCTION_REASON_FIELDS.REMOVED,
		label: 'Physically removed for return',
	},
	{
		field: STOCK_REDUCTION_REASON_FIELDS.NOT_ON_PICKLIST,
		label: 'Not on picklist',
	},
];

const toQuantity = value => Math.max(0, Math.floor(Number(value) || 0));

export const getLatestStockReductionClassification = (classifications = []) => {
	return (classifications || []).reduce((latest, classification) => {
		if (!latest) {
			return classification;
		}

		return toQuantity(classification.revision) >= toQuantity(latest.revision)
			? classification
			: latest;
	}, null);
};

export const isStockReductionQuantityChange = changes => Boolean(changes && [
	'presentProductQuantity',
	'spoiledProductQuantity',
	'removedProductQuantity',
].some(field => Object.prototype.hasOwnProperty.call(changes, field)));

export const prepareStockReductionReasonRequest = (item, changes = {}) => {
	if (item.removedProduct || !isStockReductionQuantityChange(changes)) {
		return null;
	}

	const expectedQuantity = toQuantity(item.planogramItemQuantityPostService ?? item.initialPresentProductQuantity);
	const confirmedQuantity = Object.prototype.hasOwnProperty.call(changes, 'presentProductQuantity')
		? toQuantity(changes.presentProductQuantity)
		: toQuantity(item.presentProductQuantity);
	const spoiledQuantity = Object.prototype.hasOwnProperty.call(changes, 'spoiledProductQuantity')
		? toQuantity(changes.spoiledProductQuantity)
		: toQuantity(item.spoiledProductQuantity);
	const removedQuantity = Object.prototype.hasOwnProperty.call(changes, 'removedProductQuantity')
		? toQuantity(changes.removedProductQuantity)
		: toQuantity(item.removedProductQuantity);
	const reductionQuantity = Math.max(0, expectedQuantity - confirmedQuantity);
	const unexplainedQuantity = reductionQuantity - spoiledQuantity - removedQuantity;

	if (unexplainedQuantity < STOCK_REDUCTION_REASON_THRESHOLD) {
		return null;
	}

	return {
		itemId: item.id,
		productName: item.productName || item.product?.name || 'This product',
		selection: item.selection,
		expectedQuantity,
		confirmedQuantity,
		spoiledQuantity,
		removedQuantity,
		unexplainedQuantity,
		defaultReason: item.product?.isFreshFood
			? STOCK_REDUCTION_REASON_FIELDS.SPOILED
			: STOCK_REDUCTION_REASON_FIELDS.MISSING,
	};
};

export const applyStockReductionReason = ({
	changes = {},
	request,
	reason,
}) => {
	const selectedReason = Object.values(STOCK_REDUCTION_REASON_FIELDS).includes(reason)
		? reason
		: request.defaultReason;
	const classification = {
		missingQuantity: 0,
		spoiledQuantity: request.spoiledQuantity,
		removedQuantity: request.removedQuantity,
		notPickedQuantity: 0,
	};

	classification[selectedReason] += request.unexplainedQuantity;

	return {
		...changes,
		...(selectedReason === STOCK_REDUCTION_REASON_FIELDS.SPOILED ? {
			spoiledProductQuantity: classification.spoiledQuantity,
		} : {}),
		...(selectedReason === STOCK_REDUCTION_REASON_FIELDS.REMOVED ? {
			removedProductQuantity: classification.removedQuantity,
		} : {}),
		stockReductionClassification: classification,
	};
};
